/**
 * PolicyConsentCapture — Layer 0 ("contact creation defaults") authoring view.
 *
 * Mirrors the IA of `PolicyJurisdictions` but scoped to the `consent_capture`
 * layer. Layer-0 rules grant or deny *initial INFORMATIONAL* consent at
 * contact-creation time; marketing consent is never auto-granted. Each cell
 * in the SMS / EMAIL / VOICE row holds at most one effective rule (most
 * specific wins): green pill = ALLOW, red pill = BLOCK, muted pill =
 * inherited from a parent jurisdiction. Authors edit a cell by clicking it,
 * which opens `PolicyRuleForm` pre-populated with the rule. The form already
 * locks `layer = 'consent_capture'` and hides Required Checks / Purpose /
 * B2B controls when that layer is selected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  JurisdictionPicker,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  Pencil,
  Phone,
  Plus,
  ShieldOff,
  Smartphone,
  Trash2,
  UserPlus,
} from 'lucide-react';
import type {
  Jurisdiction,
  Rule,
  PREChannel,
  MessagePurpose,
} from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { PolicyRuleForm } from '../../components/policy/PolicyRuleForm';

// ============================================
// Animation
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Constants
// ============================================

type SupportedChannel = Extract<PREChannel, 'SMS' | 'VOICE' | 'EMAIL'>;

const CHANNELS: { code: SupportedChannel; label: string; icon: React.ElementType }[] = [
  { code: 'SMS', label: 'SMS', icon: Smartphone },
  { code: 'EMAIL', label: 'Email', icon: Mail },
  { code: 'VOICE', label: 'Voice', icon: Phone },
];

const PURPOSES: { code: MessagePurpose; label: string }[] = [
  { code: 'INFORMATIONAL', label: 'Informational' },
  { code: 'MARKETING', label: 'Marketing' },
];

// ============================================
// Component
// ============================================

export function PolicyConsentCapture() {
  const jurisdictions = usePolicyStore((s) => s.jurisdictions);
  const rules = usePolicyStore((s) => s.rules);
  const isLoadingJurisdictions = usePolicyStore((s) => s.isLoadingJurisdictions);
  const isLoadingRules = usePolicyStore((s) => s.isLoadingRules);
  const loadJurisdictions = usePolicyStore((s) => s.loadJurisdictions);
  const loadRules = usePolicyStore((s) => s.loadRules);
  const deleteRule = usePolicyStore((s) => s.deleteRule);

  const [selectedCode, setSelectedCode] = useState<string | null>('US-FED');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [addContext, setAddContext] = useState<
    { channel?: SupportedChannel; purpose?: MessagePurpose } | null
  >(null);
  const [recentCodes, setRecentCodes] = useState<string[]>([]);

  useEffect(() => {
    loadJurisdictions();
    // Pull the entire ruleset so inherited rows render correctly.
    loadRules(1, 1000);
  }, [loadJurisdictions, loadRules]);

  useEffect(() => {
    if (!selectedCode) return;
    setRecentCodes((prev) => {
      if (prev[0] === selectedCode) return prev;
      return [selectedCode, ...prev.filter((c) => c !== selectedCode)].slice(0, 5);
    });
  }, [selectedCode]);

  const selectedJurisdiction = useMemo(
    () => jurisdictions.find((j) => j.code === selectedCode) ?? null,
    [jurisdictions, selectedCode],
  );

  // Ancestor chain (selected → parent → … → GLOBAL) drives inheritance.
  const ancestorChain = useMemo(() => {
    if (!selectedJurisdiction) return [] as Jurisdiction[];
    const byCode = new Map(jurisdictions.map((j) => [j.code, j]));
    const chain: Jurisdiction[] = [];
    let current: Jurisdiction | undefined = selectedJurisdiction;
    while (current) {
      chain.push(current);
      current = current.parentCode ? byCode.get(current.parentCode) : undefined;
    }
    const global = byCode.get('GLOBAL');
    if (global && !chain.some((c) => c.code === 'GLOBAL')) chain.push(global);
    return chain;
  }, [jurisdictions, selectedJurisdiction]);

  // Active consent_capture rules along the chain.
  const consentCaptureRules = useMemo(
    () =>
      rules.filter(
        (r) =>
          r.layer === 'consent_capture' &&
          r.isActive &&
          ancestorChain.some((a) => a.code === r.jurisdictionCode),
      ),
    [rules, ancestorChain],
  );

  // For each (channel × purpose), find the most-specific applicable rule.
  // Specificity order: nearest jurisdiction (earliest in ancestorChain) wins,\n  // and within the same jurisdiction, channel-pinned beats channel-null and\n  // purpose-pinned beats purpose-null, then highest priority wins.
  const ruleForCell = (
    channel: SupportedChannel,
    purpose: MessagePurpose,
  ): Rule | null => {
    for (const j of ancestorChain) {
      const candidates = consentCaptureRules.filter(
        (r) =>
          r.jurisdictionCode === j.code &&
          (r.channel === channel || r.channel === null) &&
          (r.messagePurpose === purpose || r.messagePurpose === null),
      );
      if (candidates.length === 0) continue;
      candidates.sort((a, b) => {
        const aChannelPinned = a.channel === channel ? 0 : 1;
        const bChannelPinned = b.channel === channel ? 0 : 1;
        if (aChannelPinned !== bChannelPinned)
          return aChannelPinned - bChannelPinned;
        const aPurposePinned = a.messagePurpose === purpose ? 0 : 1;
        const bPurposePinned = b.messagePurpose === purpose ? 0 : 1;
        if (aPurposePinned !== bPurposePinned)
          return aPurposePinned - bPurposePinned;
        return (b.priority ?? 0) - (a.priority ?? 0);
      });
      return candidates[0] ?? null;
    }
    return null;
  };

  const localRulesAtSelected = consentCaptureRules.filter(
    (r) => r.jurisdictionCode === selectedCode,
  );
  const inheritedCount = consentCaptureRules.length - localRulesAtSelected.length;

  const isLoading = isLoadingJurisdictions || isLoadingRules;

  const handleDeleteRule = async (rule: Rule) => {
    if (!confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    await deleteRule(rule.id);
  };

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col h-full"
      >
        {/* Header */}
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center gap-4">
            <Link
              to="/settings/policy"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Contact Creation Defaults
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Per (channel × purpose) ALLOW/BLOCK rules that decide whether
                consent is auto-granted when a contact is created. The engine
                defaults to ALLOW for every cell &mdash; add a rule only when
                a jurisdiction restricts auto-grant.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Picker bar */}
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 px-6 py-4 flex flex-wrap items-center gap-3 border-b border-slate-200 dark:border-slate-800"
        >
          <div className="min-w-[260px] max-w-md flex-1">
            <JurisdictionPicker
              jurisdictions={jurisdictions}
              value={selectedCode}
              onChange={setSelectedCode}
              recentCodes={recentCodes}
              isLoading={isLoadingJurisdictions}
            />
          </div>
          {selectedJurisdiction && (
            <>
              <Badge variant="outline" className="tabular-nums">
                {localRulesAtSelected.length} local
              </Badge>
              {inheritedCount > 0 && (
                <Badge variant="secondary" className="tabular-nums">
                  {inheritedCount} inherited
                </Badge>
              )}
              {selectedJurisdiction.parentCode && (
                <Badge variant="outline" className="text-[11px]">
                  inherits from {selectedJurisdiction.parentCode}
                </Badge>
              )}
              <div className="ml-auto">
                <Button size="sm" onClick={() => setAddContext({})}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add rule
                </Button>
              </div>
            </>
          )}
        </motion.div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Banner */}
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10 p-4 text-sm text-emerald-900 dark:text-emerald-200">
            <p className="font-semibold mb-1">Layer 0 &mdash; Consent Capture</p>
            <p className="text-[13px] leading-relaxed">
              When a contact is created, Respectly evaluates these rules per
              (channel &times; purpose) and writes a starting{' '}
              <code className="text-[12px] px-1 py-0.5 rounded bg-emerald-100/80 dark:bg-emerald-800/40">
                ConsentRecord(status=EXPRESS_OPTIN, captureContext=AUTO_JURISDICTION)
              </code>{' '}
              wherever no rule blocks it. The engine is wide-open by default;
              add a BLOCK rule for any cell where a jurisdiction restricts
              auto-grant (e.g. Marketing in most US states).
            </p>
          </div>

          {isLoading && rules.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : !selectedJurisdiction ? (
            <p className="text-sm text-slate-400 text-center py-12">
              Pick a jurisdiction to see its consent-capture rules.
            </p>
          ) : (
            <div className="space-y-6">
              {PURPOSES.map((purpose) => (
                <div key={purpose.code}>
                  <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 uppercase tracking-wider">
                    {purpose.label}
                  </h2>
                  <div className="grid gap-4 md:grid-cols-3">
                    {CHANNELS.map((channel) => {
                      const rule = ruleForCell(channel.code, purpose.code);
                      return (
                        <ChannelCell
                          key={`${channel.code}-${purpose.code}`}
                          channel={channel}
                          purpose={purpose}
                          rule={rule}
                          selectedCode={selectedCode!}
                          onEdit={(r) => setEditingRule(r)}
                          onDelete={handleDeleteRule}
                          onAdd={(c, p) => setAddContext({ channel: c, purpose: p })}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Edit modal */}
      <PolicyRuleForm
        isOpen={!!editingRule}
        onClose={() => setEditingRule(null)}
        rule={editingRule}
        jurisdictionCode={editingRule?.jurisdictionCode ?? selectedCode ?? 'GLOBAL'}
      />

      {/* Add modal — pre-populated with the cell's channel + purpose when
          launched from a ChannelCell, blank when launched from the top bar. */}
      <PolicyRuleForm
        isOpen={!!addContext}
        onClose={() => setAddContext(null)}
        rule={null}
        jurisdictionCode={selectedCode ?? 'GLOBAL'}
        defaultChannel={addContext?.channel}
        defaultPurpose={addContext?.purpose}
        defaultLayer="consent_capture"
      />
    </>
  );
}

// ============================================
// ChannelCell — single SMS/EMAIL/VOICE tile
// ============================================

interface ChannelCellProps {
  channel: { code: SupportedChannel; label: string; icon: React.ElementType };
  purpose: { code: MessagePurpose; label: string };
  rule: Rule | null;
  selectedCode: string;
  onEdit: (rule: Rule) => void;
  onDelete: (rule: Rule) => void;
  onAdd: (channel: SupportedChannel, purpose: MessagePurpose) => void;
}

function ChannelCell({
  channel,
  purpose,
  rule,
  selectedCode,
  onEdit,
  onDelete,
  onAdd,
}: ChannelCellProps) {
  const ChannelIcon = channel.icon;
  const isInherited = !!rule && rule.jurisdictionCode !== selectedCode;
  // Default-allow: when no rule, the engine grants consent. Border + badge
  // reflect that; only an explicit BLOCK rule turns the cell red.
  const isAllow = rule ? rule.effect === 'ALLOW' : true;

  const borderClass = isAllow
    ? 'border-emerald-300 dark:border-emerald-800'
    : 'border-rose-300 dark:border-rose-800';

  return (
    <div
      className={cn(
        'relative rounded-2xl border-2 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow',
        borderClass,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            <ChannelIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {channel.label}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              {purpose.label} consent at contact creation
            </p>
          </div>
        </div>
        <Badge
          variant={isAllow ? 'success' : 'error'}
          className="text-[11px] flex-shrink-0"
        >
          {isAllow ? (
            <>
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Allow
            </>
          ) : (
            <>
              <ShieldOff className="w-3 h-3 mr-1" />
              Block
            </>
          )}
        </Badge>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        {!rule ? (
          <p className="text-[13px] text-slate-500 dark:text-slate-400">
            No rule. Default behaviour:{' '}
            <strong className="text-emerald-700 dark:text-emerald-400">
              ALLOW
            </strong>{' '}
            &mdash; consent is auto-granted.
          </p>
        ) : (
          <>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {rule.name}
            </p>
            {rule.description && (
              <p className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
                {rule.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {rule.sourceRef && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  {rule.sourceRef}
                </Badge>
              )}
              {isInherited && (
                <Badge variant="secondary" className="text-[10px]">
                  inherits from {rule.jurisdictionCode}
                </Badge>
              )}
              {!isInherited && (
                <Badge variant="outline" className="text-[10px]">
                  local
                </Badge>
              )}
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      {rule ? (
        <div className="flex items-center justify-end gap-1 px-3 py-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={() => onEdit(rule)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[12px] text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={`Edit ${channel.label} rule`}
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          {!isInherited && (
            <button
              type="button"
              onClick={() => onDelete(rule)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[12px] text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 rounded hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
              aria-label={`Delete ${channel.label} rule`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-end gap-1 px-3 py-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={() => onAdd(channel.code, purpose.code)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[12px] text-emerald-700 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
            aria-label={`Add ${purpose.label.toLowerCase()} ${channel.label} rule`}
          >
            <Plus className="w-3.5 h-3.5" />
            Add rule
          </button>
        </div>
      )}
    </div>
  );
}
